import { TellescopeModelFromName } from "../../mappings";

const DEFAULT_INBOX_STATUS = "New";

/**
 * Converts a TicketThreadComment to a partial InboxThread representation.
 *
 * IMPORTANT LIMITATIONS:
 * - TicketThreadComments do NOT contain full thread metadata (assignedTo, userIds, title, etc.)
 * - The TicketThread is the actual source of thread information for Zendesk type
 * - This utility derives what it can from the comment alone
 * - For complete thread information, use convertTicketThreadToInboxThread instead
 *
 * Purpose: Used for cache invalidation when a new comment is created but you
 * don't have the full TicketThread data. This helps determine if the comment could
 * affect cached inbox_threads based on timestamp, preview, and threadId.
 *
 * @param comment - The TicketThreadComment to convert
 * @returns A partial InboxThread representation derived from the comment
 */
export const convertTicketThreadCommentToInboxThread = (
  comment: TellescopeModelFromName<"ticket_thread_comments">
): Pick<
  TellescopeModelFromName<"inbox_threads">,
  | "type"
  | "threadId"
  | "preview"
  | "timestamp"
  | "inboxStatus"
  | "readBy"
  | "outboundPreview"
  | "outboundTimestamp"
  | "recentOutboundUserId"
  | "recentInboundEnduserId"
> => {
  const isInbound = comment.inbound;
  const previewText = comment.plaintext || comment.html?.replace(/<[^>]*>/g, "").trim() || "";

  return {
    type: "Zendesk",
    threadId: comment.ticketThreadId,
    preview: isInbound ? previewText : "",
    timestamp: new Date(comment.timestamp || comment.createdAt || new Date()),
    inboxStatus: DEFAULT_INBOX_STATUS,
    readBy: {},
    outboundPreview: !isInbound ? previewText : "",
    outboundTimestamp: !isInbound ? new Date(comment.timestamp || comment.createdAt || new Date()) : undefined,
    ...(!isInbound && comment.userId ? { recentOutboundUserId: comment.userId } : {}),
    ...(isInbound && comment.enduserId ? { recentInboundEnduserId: comment.enduserId } : {}),
  };
};
